import type { AppViewState } from "./app-view-state.ts";
import { loadConfig } from "./controllers/config.ts";
import { saveConfigPatch } from "./controllers/config.ts";
import { cloneConfigObject, setPathValue } from "./controllers/config/form-utils.ts";
import { nativeConfirm } from "./native-dialog-bridge.ts";
import { t } from "./strings.ts";
import type { AddModelForm, AddProviderForm, ModelDefinitionEntry, ModelProvider } from "./views/models.ts";
import { BUILTIN_PROVIDERS } from "./views/models-builtin.ts";

function setSelectedProvider(host: AppViewState, key: string | null) {
  host.modelsSelectedProvider = key;
  host.modelLibrarySelectedProvider = key;
}

export function handleModelsRefresh(host: AppViewState) {
  loadConfig(host);
}

export function handleModelsAddProvider(host: AppViewState) {
  host.modelsAddProviderModalOpen = true;
  host.modelsAddProviderForm = {
    providerId: "",
    displayName: "",
    baseUrl: "",
    apiKey: "",
    apiKeyPrefix: "",
  };
}

export function handleModelsAddProviderModalClose(host: AppViewState) {
  host.modelsAddProviderModalOpen = false;
}

export function handleModelsAddProviderFormChange(host: AppViewState, form: Partial<AddProviderForm>) {
  host.modelsAddProviderForm = { ...host.modelsAddProviderForm, ...form };
}

export function handleModelsAddProviderSubmit(host: AppViewState) {
  const { providerId, displayName, baseUrl, apiKey, apiKeyPrefix } = host.modelsAddProviderForm;
  if (!providerId.trim() || !displayName.trim()) return;
  const key = providerId.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9_-]/g, "");
  if (!key) return;
  if (!host.configForm && host.configSnapshot?.config) {
    host.configForm = cloneConfigObject(host.configSnapshot.config as Record<string, unknown>);
  }
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {});
  if (!base.models) {
    base.models = { mode: "merge", providers: {} };
  }
  const models = base.models as { mode?: string; providers?: Record<string, ModelProvider> };
  if (!models.providers) {
    models.providers = {};
  }
  if (models.providers[key]) {
    host.modelsAddProviderModalOpen = false;
    setSelectedProvider(host, key);
    return;
  }
  models.providers[key] = {
    displayName: displayName.trim(),
    baseUrl: baseUrl.trim() || undefined,
    apiKey: apiKey.trim() || undefined,
    apiKeyPrefix: apiKeyPrefix.trim() || undefined,
    api: "openai-completions",
  };
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
  host.modelsAddProviderModalOpen = false;
  setSelectedProvider(host, key);
}

export function handleModelsSelect(host: AppViewState, key: string | null) {
  setSelectedProvider(host, key);
}

export function handleModelsPatch(host: AppViewState, key: string, patch: Partial<ModelProvider>) {
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {});
  if (!base.models) {
    base.models = { mode: "merge", providers: {} };
  }
  const models = base.models as { mode?: string; providers?: Record<string, ModelProvider> };
  if (!models.providers) {
    models.providers = {};
  }
  const current = models.providers[key] ?? {};
  models.providers[key] = { ...current, ...patch };
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
}

function parseOptionalPositiveInt(s: string): number | undefined {
  const t = s.trim();
  if (!t) return undefined;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return undefined;
  return n;
}

export function handleModelsAddModel(host: AppViewState, providerKey: string) {
  host.modelsAddModelModalOpen = true;
  host.modelsAddModelForm = { modelId: "", modelName: "", contextWindow: "", maxTokens: "" };
}

export function handleModelsAddModelModalClose(host: AppViewState) {
  host.modelsAddModelModalOpen = false;
}

export function handleModelsAddModelFormChange(host: AppViewState, form: Partial<AddModelForm>) {
  host.modelsAddModelForm = { ...host.modelsAddModelForm, ...form };
}

export function handleModelsAddModelSubmit(host: AppViewState, providerKey: string) {
  const { modelId, modelName, contextWindow, maxTokens } = host.modelsAddModelForm;
  if (!modelId.trim() || !modelName.trim()) return;
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {});
  if (!base.models) {
    base.models = { mode: "merge", providers: {} };
  }
  const models = base.models as { mode?: string; providers?: Record<string, ModelProvider> };
  if (!models.providers) {
    models.providers = {};
  }
  const prov = models.providers[providerKey] ?? {};
  const existing = prov.models ?? [];
  if (existing.some((m) => m.id === modelId.trim())) {
    host.modelsAddModelModalOpen = false;
    return;
  }
  const cw = parseOptionalPositiveInt(contextWindow);
  const mt = parseOptionalPositiveInt(maxTokens);
  const entry: ModelDefinitionEntry = { id: modelId.trim(), name: modelName.trim() };
  if (cw !== undefined) entry.contextWindow = cw;
  if (mt !== undefined) entry.maxTokens = mt;
  models.providers[providerKey] = {
    ...prov,
    models: [...existing, entry],
  };
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
  host.modelsAddModelModalOpen = false;
}

export function handleModelsPatchModel(
  host: AppViewState,
  providerKey: string,
  modelId: string,
  patch: Partial<{ id: string; name: string; contextWindow: number | null; maxTokens: number | null }>,
) {
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {}) as {
    models?: { providers?: Record<string, ModelProvider> };
    agents?: { defaults?: { model?: { primary?: string } } };
    env?: { modelEnv?: Record<string, Record<string, string>> };
  };
  if (!base.models) {
    base.models = { mode: "merge", providers: {} };
  }
  const models = base.models as { mode?: string; providers?: Record<string, ModelProvider> };
  if (!models.providers) {
    models.providers = {};
  }
  const prov = models.providers[providerKey];
  if (!prov?.models?.length) return;

  let nextModelId = modelId;
  if ("id" in patch) {
    const trimmed = patch.id?.trim() ?? "";
    if (!trimmed || trimmed === modelId) {
      // no-op
    } else if (prov.models.some((m) => m.id === trimmed)) {
      return;
    } else {
      nextModelId = trimmed;
      const oldRef = `${providerKey}/${modelId}`;
      const newRef = `${providerKey}/${nextModelId}`;
      const currentDefaultRef = base.agents?.defaults?.model?.primary;
      if (currentDefaultRef === oldRef) {
        if (!base.agents) base.agents = { defaults: { model: {} } };
        if (!base.agents.defaults) base.agents.defaults = { model: {} };
        if (!base.agents.defaults.model) base.agents.defaults.model = {};
        base.agents.defaults.model.primary = newRef;
      }
      const envEntry = base.env?.modelEnv?.[oldRef];
      if (envEntry && base.env?.modelEnv) {
        base.env.modelEnv[newRef] = envEntry;
        delete base.env.modelEnv[oldRef];
      }
    }
  }

  const nextModels = prov.models.map((m) => {
    if (m.id !== modelId) return m;
    const u = { ...m, id: nextModelId };
    if ("name" in patch) {
      u.name = patch.name?.trim() || u.id;
    }
    if ("contextWindow" in patch) {
      if (patch.contextWindow == null) delete u.contextWindow;
      else u.contextWindow = patch.contextWindow;
    }
    if ("maxTokens" in patch) {
      if (patch.maxTokens == null) delete u.maxTokens;
      else u.maxTokens = patch.maxTokens;
    }
    return u;
  });
  models.providers[providerKey] = { ...prov, models: nextModels };
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
}

export function handleModelsPatchModelEnv(host: AppViewState, providerKey: string, modelId: string, envVars: Record<string, string>) {
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {});
  if (!base.env) {
    base.env = { vars: {}, modelEnv: {} };
  }
  const env = base.env as { vars?: Record<string, string>; modelEnv?: Record<string, Record<string, string>> };
  if (!env.modelEnv) {
    env.modelEnv = {};
  }
  const modelRef = `${providerKey}/${modelId}`;
  // Keep __new__ in form state for UI add-row placeholder; filtered on save
  env.modelEnv[modelRef] = { ...envVars };
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
}

export function handleModelsRemoveModel(host: AppViewState, providerKey: string, modelId: string) {
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {}) as {
    models?: { providers?: Record<string, ModelProvider> };
    agents?: { defaults?: { model?: { primary?: string } } };
    env?: { modelEnv?: Record<string, Record<string, string>> };
  };
  const providers = base.models?.providers;
  if (!providers) return;
  const prov = providers[providerKey];
  if (!prov?.models) return;

  // 检查删除的模型是否是当前默认模型
  const deletedModelRef = `${providerKey}/${modelId}`;
  const currentDefaultRef = base.agents?.defaults?.model?.primary;
  if (currentDefaultRef === deletedModelRef) {
    // 清除默认模型设置
    if (base.agents?.defaults?.model) {
      delete base.agents.defaults.model.primary;
    }
  }

  providers[providerKey] = {
    ...prov,
    models: prov.models.filter((m: { id: string }) => m.id !== modelId),
  };
  const modelRef = `${providerKey}/${modelId}`;
  if (base.env?.modelEnv?.[modelRef]) {
    const nextModelEnv = { ...base.env.modelEnv };
    delete nextModelEnv[modelRef];
    base.env.modelEnv = nextModelEnv;
  }
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
}

function collectEnvVarsFromProviders(providers: Record<string, ModelProvider>): Record<string, string> {
  const collected: Record<string, string> = {};
  for (const prov of Object.values(providers)) {
    const ev = prov.envVars ?? {};
    for (const [k, v] of Object.entries(ev)) {
      if (!k || k === "__new__") continue;
      if (collected[k] !== undefined && collected[k] !== v) {
        return { __conflict: k };
      }
      collected[k] = v;
    }
  }
  return collected;
}

function sanitizeProviderForSave(prov: ModelProvider): ModelProvider {
  const ev = prov.envVars ?? {};
  const sanitized: Record<string, string> = {};
  for (const [k, v] of Object.entries(ev)) {
    if (k && k !== "__new__") sanitized[k] = v;
  }
  return { ...prov, envVars: Object.keys(sanitized).length ? sanitized : undefined };
}

export function handleModelsSave(host: AppViewState) {
  host.modelsSaveError = null;
  const providers = (host.configForm?.models as { providers?: Record<string, ModelProvider> })?.providers ?? {};
  const conflict = collectEnvVarsFromProviders(providers);
  if (conflict.__conflict) {
    host.modelsSaveError = conflict.__conflict;
    return;
  }
  const existingEnv = (host.configForm?.env as { vars?: Record<string, string> })?.vars ?? {};
  const mergedEnv = { ...existingEnv, ...conflict };
  const sanitizedProviders: Record<string, ModelProvider> = {};
  for (const [k, v] of Object.entries(providers)) {
    let prov = sanitizeProviderForSave(v);
    const builtin = BUILTIN_PROVIDERS.find((p) => p.id === k);
    if (builtin) {
      if (!prov.baseUrl || prov.baseUrl.trim() === "") {
        prov = { ...prov, baseUrl: builtin.baseUrl };
      }
      if (!prov.api || prov.api.trim() === "") {
        const defaultApi = builtin.defaultApi ?? "openai-completions";
        prov = { ...prov, api: defaultApi };
      }
    }
    sanitizedProviders[k] = prov;
  }
  const existingModels =
    host.configForm?.models && typeof host.configForm.models === "object" && !Array.isArray(host.configForm.models)
      ? (host.configForm.models as Record<string, unknown>)
      : {};
  const patch: Record<string, unknown> = {
    models: { ...existingModels, providers: sanitizedProviders },
  };
  // 如果默认模型被修改（如删除默认模型时清除），也需要保存 agents
  if (host.configForm?.agents) {
    patch.agents = host.configForm.agents;
  }
  const envForm = host.configForm?.env as { vars?: Record<string, string>; modelEnv?: Record<string, Record<string, string>> } | undefined;
  const modelEnv = envForm?.modelEnv ?? {};
  const originalEnv = host.configFormOriginal?.env as
    | { modelEnv?: Record<string, Record<string, string>> }
    | undefined;
  const originalModelEnv = originalEnv?.modelEnv ?? {};
  const sanitizedModelEnv: Record<string, Record<string, string> | null> = {};
  for (const [k, v] of Object.entries(modelEnv)) {
    if (!v || typeof v !== "object") continue;
    const sanitized: Record<string, string> = {};
    for (const [ek, ev] of Object.entries(v)) {
      if (ek && ek !== "__new__") sanitized[ek] = ev;
    }
    if (Object.keys(sanitized).length > 0) {
      sanitizedModelEnv[k] = sanitized;
    } else {
      sanitizedModelEnv[k] = null;
    }
  }
  for (const key of Object.keys(originalModelEnv)) {
    if (!(key in modelEnv)) {
      sanitizedModelEnv[key] = null;
    }
  }
  patch.env = { vars: mergedEnv, modelEnv: sanitizedModelEnv };
  saveConfigPatch(host, patch);
  host.modelsFormDirty = false;
  setSelectedProvider(host, null);
}

export function handleModelsCancel(host: AppViewState) {
  setSelectedProvider(host, null);
  host.modelsSaveError = null;
  if (host.modelsFormDirty) {
    // 重置脏状态，确保 loadConfig 能正确重置 configForm
    host.modelsFormDirty = false;
    host.configFormDirty = false;
    loadConfig(host);
  }
}

export function handleModelsUseModelClick(host: AppViewState, provider: string) {
  host.modelsUseModelModalOpen = true;
  host.modelsUseModelModalProvider = provider;
}

export function handleModelsUseModelModalClose(host: AppViewState) {
  host.modelsUseModelModalOpen = false;
  host.modelsUseModelModalProvider = null;
}

export function handleModelsUseModel(host: AppViewState, provider: string, modelId: string) {
  const modelRef = `${provider}/${modelId}`;
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {}) as Record<string, unknown>;
  setPathValue(base, ["agents", "defaults", "model", "primary"], modelRef);
  host.configForm = base;
  host.configFormDirty = true;
  saveConfigPatch(host, { agents: base.agents });
  host.modelsUseModelModalOpen = false;
  host.modelsUseModelModalProvider = null;
}

export function handleModelsCancelUse(host: AppViewState, provider: string) {
  const current = (host.configForm?.agents as Record<string, unknown>)?.defaults as Record<string, unknown> | undefined;
  const model = current?.model;
  const primary = model && typeof model === "object" && !Array.isArray(model)
    ? (model as Record<string, unknown>).primary
    : undefined;
  const currentRef = typeof primary === "string" ? primary : null;
  if (!currentRef || !currentRef.startsWith(provider + "/")) return;
  // Backend mergePatch deletes key when patch value is nil; send primary: null to remove it
  const patch: Record<string, unknown> = {
    agents: {
      defaults: {
        model: { primary: null },
      },
    },
  };
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {}) as Record<string, unknown>;
  const agents = base.agents as Record<string, unknown> | undefined;
  const defaults = agents?.defaults as Record<string, unknown> | undefined;
  const modelObj = defaults?.model;
  if (modelObj && typeof modelObj === "object" && !Array.isArray(modelObj)) {
    delete (modelObj as Record<string, unknown>).primary;
  }
  host.configForm = base;
  host.configFormDirty = true;
  saveConfigPatch(host, patch);
}

function buildModelsDeleteProviderPatch(
  config: {
    models?: { providers?: Record<string, ModelProvider> };
    env?: { modelEnv?: Record<string, Record<string, string>> };
    agents?: { defaults?: { model?: { primary?: string } } };
  },
  providerKey: string,
): Record<string, unknown> {
  const currentProviders = config.models?.providers ?? {};
  const providerModels = currentProviders[providerKey]?.models ?? [];
  const currentModelEnv = config.env?.modelEnv ?? {};
  const currentDefaultRef = config.agents?.defaults?.model?.primary;

  const providerPatch: Record<string, ModelProvider | null> = {
    [providerKey]: null,
  };
  const modelEnvPatch: Record<string, null> = {};

  for (const model of providerModels) {
    modelEnvPatch[`${providerKey}/${model.id}`] = null;
  }
  for (const modelRef of Object.keys(currentModelEnv)) {
    if (modelRef.startsWith(`${providerKey}/`)) {
      modelEnvPatch[modelRef] = null;
    }
  }

  const patch: Record<string, unknown> = {
    models: {
      providers: providerPatch,
    },
  };
  if (Object.keys(modelEnvPatch).length > 0) {
    patch.env = {
      modelEnv: modelEnvPatch,
    };
  }
  if (currentDefaultRef && currentDefaultRef.startsWith(`${providerKey}/`)) {
    patch.agents = {
      defaults: {
        model: {
          primary: null,
        },
      },
    };
  }
  return patch;
}

function applyModelsDeleteProviderToForm(host: AppViewState, providerKey: string) {
  const base = cloneConfigObject(host.configForm ?? host.configSnapshot?.config ?? {}) as {
    models?: { providers?: Record<string, ModelProvider> };
    env?: { modelEnv?: Record<string, Record<string, string>> };
    agents?: { defaults?: { model?: { primary?: string } } };
  };
  if (base.models?.providers) {
    delete base.models.providers[providerKey];
  }
  const modelEnv = base.env?.modelEnv;
  if (modelEnv) {
    for (const modelRef of Object.keys(modelEnv)) {
      if (modelRef.startsWith(`${providerKey}/`)) {
        delete modelEnv[modelRef];
      }
    }
  }
  const currentDefaultRef = base.agents?.defaults?.model?.primary;
  if (currentDefaultRef && currentDefaultRef.startsWith(`${providerKey}/`)) {
    delete base.agents?.defaults?.model?.primary;
  }
  host.configForm = base;
  host.configFormDirty = true;
  host.modelsFormDirty = true;
}

export async function handleModelsDeleteProvider(host: AppViewState, providerKey?: string) {
  const key = providerKey?.trim() || host.modelLibrarySelectedProvider || host.modelsSelectedProvider;
  if (!key) return;

  const ok = await nativeConfirm(t("modelsProviderDeleteConfirm"));
  if (!ok) return;

  const currentConfig = (host.configForm ?? host.configSnapshot?.config ?? {}) as {
    models?: { providers?: Record<string, ModelProvider> };
    env?: { modelEnv?: Record<string, Record<string, string>> };
    agents?: { defaults?: { model?: { primary?: string } } };
  };
  const patch = buildModelsDeleteProviderPatch(currentConfig, key);

  if (host.client && host.connected) {
    await saveConfigPatch(host, patch);
    if (host.lastError) {
      return;
    }
  } else {
    applyModelsDeleteProviderToForm(host, key);
  }

  if (host.modelLibrarySelectedProvider === key || host.modelsSelectedProvider === key) {
    setSelectedProvider(host, null);
  }
}
